import React, { useEffect } from 'react';
import { X, Image as ImageIcon, Film } from 'lucide-react';
import { formatMediaUrl } from '../api/media-api';

/**
 * MediaPreviewModal
 * Full-screen lightbox modal for viewing high-res images or playing videos.
 *
 * Props:
 *   mediaItem - { url, type, filename, mimeType }
 *   onClose   - function to close the preview modal
 */
const MediaPreviewModal = ({ mediaItem, onClose }) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  if (!mediaItem) return null;

  const isVideo = mediaItem.type === 'VIDEO' || mediaItem.mimeType?.startsWith('video');

  // Prefer the local preview. While a case is being written, `url` holds a
  // storage key the moment its upload finishes — not a fetchable address — and
  // rendering it gives a blank player. Media loaded from the API has no
  // previewUrl and arrives already signed, so it falls through to `url`.
  const resolved = formatMediaUrl(mediaItem.previewUrl || mediaItem.url);
  // A storage key is not an address. A draft restored after a page load holds
  // only the key, so show an empty player rather than a failed request.
  const src = /^(https?:|blob:|data:)/.test(resolved || '') ? resolved : undefined;

  return (
    <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-md flex flex-col justify-between animate-fadeIn p-4 sm:p-6">
      {/* Header */}
      <div className="flex items-center justify-between text-white shrink-0 mb-3 max-w-5xl w-full mx-auto">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
            {isVideo ? <Film size={16} className="text-purple-400" /> : <ImageIcon size={16} className="text-indigo-400" />}
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold truncate leading-tight">
              {mediaItem.filename || (isVideo ? 'Video Preview' : 'Image Preview')}
            </h3>
            <span className="text-[11px] text-gray-400 font-medium">
              {isVideo ? 'Interactive Video Player' : 'High Resolution Image'}
            </span>
          </div>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="p-2 text-gray-300 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-colors shrink-0"
          title="Close preview (Esc)"
        >
          <X size={20} />
        </button>
      </div>

      {/* Media Content */}
      <div className="flex-1 flex items-center justify-center min-h-0 relative max-w-5xl w-full mx-auto my-auto py-2 overflow-hidden">
        {isVideo ? (
          <video
            src={src}
            controls
            autoPlay
            playsInline
            className="max-w-full max-h-full rounded-2xl shadow-2xl bg-black object-contain border border-white/10"
          />
        ) : (
          <img
            src={src}
            alt={mediaItem.filename || 'Preview'}
            className="max-w-full max-h-full rounded-2xl shadow-2xl object-contain border border-white/10"
          />
        )}
      </div>

      {/* Footer / Caption */}
      <div className="shrink-0 text-center py-2">
        <p className="text-xs text-gray-400 font-medium">
          Press <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-[10px] text-gray-300 font-mono">ESC</kbd> or click Anywhere Outside to exit preview
        </p>
      </div>
    </div>
  );
};

export default MediaPreviewModal;
